import {
  decryptLocalStorageValue,
  encryptLocalStorageValue,
  hashLocalStorageKeyName,
  isEncryptedStorageValue,
  isHashedStorageKeyName,
  isMsalStorageKey,
  LocalStorageEncryptionKey,
  LocalStorageWritableValue,
  normalizeLocalStorageValue,
} from './local-storage-crypto.util';
import {
  getLocalStorageEncryptionKey,
  isLocalStorageEncryptionKeyReady,
} from './local-storage-key-state.util';

export interface NativeBrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  key(index: number): string | null;
  readonly length: number;
}

type StorageKind = 'local' | 'session';

// Temporary memory — stores last read value so we don't decrypt same key again and again
const readCache = new Map<string, string>();

/**
 * Make unique cache id for a storage key.
 * Parameter kind     → 'local' or 'session' (from patchStorage)
 * Parameter plainKey → key name from screen code, example "hawbId"
 */
function cacheId(kind: StorageKind, plainKey: string): string {
  return `${kind}:${plainKey}`;
}

/**
 * Clear all cached read values from memory.
 * Called after logout, migration, or when storage data changes.
 */
export function clearEncryptedStorageReadCache(): void {
  readCache.clear();
}

/**
 * Remove one key from read cache (value was changed or deleted).
 * Parameter kind     → 'local' or 'session'
 * Parameter plainKey → key name from screen code
 */
function invalidateCache(kind: StorageKind, plainKey: string): void {
  readCache.delete(cacheId(kind, plainKey));
}

/**
 * Encryption 2.0.2 — Safely encrypt value. Returns null if encryption fails.
 *
 * Parameter plainText     → normal value to save, from writeEncryptedStorageItem
 * Parameter encryptionKey → from getLocalStorageEncryptionKey() (Key 1.0.4)
 */
function safeEncrypt(
  plainText: LocalStorageWritableValue,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  try {
    // Call Encryption 2.0.4
    return encryptLocalStorageValue(plainText, encryptionKey);
  } catch {
    // Encryption failed — caller will save plain text as backup
    return null;
  }
}

/**
 * Decryption 3.0.3 — Safely decrypt value. Returns null if decryption fails.
 *
 * Parameter cipherText    → scrambled text from browser storage
 * Parameter encryptionKey → same password used when saving, from Key 1.0.4
 */
function safeDecrypt(
  cipherText: string,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  try {
    // Call Decryption 3.0.4
    return decryptLocalStorageValue(cipherText, encryptionKey);
  } catch {
    return null;
  }
}

/**
 * Decryption 3.0.5 — Handle old localStorage data saved before encryption was added.
 *
 * Parameter legacyValue → value read from browser (may be plain or already encrypted)
 * Parameter encryptionKey → from getLocalStorageEncryptionKey()
 */
function resolveLegacyPlainValue(
  legacyValue: string,
  encryptionKey: LocalStorageEncryptionKey
): string {
  // Value is normal text — return as is
  if (!isEncryptedStorageValue(legacyValue)) {
    return legacyValue;
  }

  // Value is encrypted — decrypt it
  const decrypted = safeDecrypt(legacyValue, encryptionKey);
  // Return decrypted text, or original if decrypt failed
  return decrypted ?? legacyValue;
}

/**
 * Decryption 3.0.2 — Read one item from browser and return normal (decrypted) value.
 *
 * Parameter native        → real browser storage (from createNativeBrowserStorage)
 * Parameter plainKey      → key name from screen code, example "hawbId"
 * Parameter encryptionKey → from getLocalStorageEncryptionKey() (Key 1.0.4)
 *
 * Example: screen asks for "hawbId" → returns "12345"
 * Browser actually has: "8f3a2b1c..." = "U2FsdGVkX1..."
 */
function readEncryptedValue(
  native: NativeBrowserStorage,
  plainKey: string,
  encryptionKey: LocalStorageEncryptionKey
): string | null {
  // Step 1 — hide name "hawbId" to find it in browser (Encryption 2.0.3)
  const hashedKey = hashLocalStorageKeyName(plainKey, encryptionKey);
  // Step 2 — read scrambled value from browser using hidden name
  const encrypted = native.getItem(hashedKey);

  // Step 3 — if found, decrypt back to normal text
  if (encrypted !== null) {
    const decrypted = safeDecrypt(encrypted, encryptionKey);
    if (decrypted !== null) {
      return decrypted;
    }
  }

  // Step 4 — maybe old data still saved with plain name "hawbId" (before encryption feature)
  const legacy = native.getItem(plainKey);
  if (legacy === null) {
    return null;
  }

  // Step 5 — get real value from old data
  const plainValue = resolveLegacyPlainValue(legacy, encryptionKey);
  // Step 6 — encrypt it properly now and save under hidden name
  const encryptedLegacy = safeEncrypt(plainValue, encryptionKey);
  if (encryptedLegacy !== null) {
    native.setItem(hashedKey, encryptedLegacy);
    native.removeItem(plainKey);
  }

  return plainValue;
}

// True when data was saved as plain text before password was ready — needs converting later
let pendingPlainKeyMigration = false;

/**
 * Migration 4.0.1 — Mark that plain localStorage data needs converting to encrypted.
 * Called when screen saves data before user finishes login.
 */
export function schedulePlainStorageKeyMigration(): void {
  pendingPlainKeyMigration = true;
  // If password already ready, convert now
  if (isLocalStorageEncryptionKeyReady()) {
    flushPendingPlainStorageKeyMigration();
  }
}

/**
 * Migration 4.0.2 — Convert pending plain data once password is ready.
 *
 * Parameter nativeLocalStorage  → real localStorage (optional, from getNativeLocalStorage)
 * Parameter nativeSessionStorage → real sessionStorage (optional, from getNativeSessionStorage)
 */
export function flushPendingPlainStorageKeyMigration(
  nativeLocalStorage?: NativeBrowserStorage,
  nativeSessionStorage?: NativeBrowserStorage
): void {
  // Nothing to do if no pending data or password not ready
  if (!pendingPlainKeyMigration || !isLocalStorageEncryptionKeyReady()) {
    return;
  }

  if (nativeLocalStorage && nativeSessionStorage) {
    migratePlainStorageKeys(nativeLocalStorage);
    migratePlainStorageKeys(nativeSessionStorage);
  }

  pendingPlainKeyMigration = false;
  clearEncryptedStorageReadCache();
}

/**
 * Migration 4.0.3 — Loop all keys in storage and encrypt plain ones.
 *
 * Parameter native → real browser storage (localStorage or sessionStorage)
 *
 * Skips Microsoft keys (msal.*) and already hidden names.
 * For each plain key like "hawbId": encrypt value, save under hidden name, delete plain name.
 */
export function migratePlainStorageKeys(native: NativeBrowserStorage): void {
  if (!isLocalStorageEncryptionKeyReady()) {
    return;
  }

  // Key 1.0.4 — get secret password from memory
  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    return;
  }

  // Collect all key names currently in browser storage
  const storedKeys: string[] = [];
  for (let index = 0; index < native.length; index += 1) {
    const key = native.key(index);
    if (key) {
      storedKeys.push(key);
    }
  }

  // Process each key
  storedKeys.forEach(storedKey => {
    // Skip Microsoft login keys and already encrypted key names
    if (isMsalStorageKey(storedKey) || isHashedStorageKeyName(storedKey)) {
      return;
    }

    // Read old value
    const legacyValue = native.getItem(storedKey);
    if (legacyValue === null) {
      return;
    }

    // Get normal value (decrypt if old value was already encrypted)
    const plainValue = resolveLegacyPlainValue(legacyValue, encryptionKey);
    // Hide the key name
    const hashedKey = hashLocalStorageKeyName(storedKey, encryptionKey);
    // Encrypt the value
    const encrypted = safeEncrypt(plainValue, encryptionKey);

    if (encrypted === null) {
      return;
    }

    // Save encrypted value under hidden name (only if not already saved)
    if (native.getItem(hashedKey) === null) {
      native.setItem(hashedKey, encrypted);
    }

    // Delete old plain key name
    native.removeItem(storedKey);
  });
}

/**
 * Migration 4.0.4 — Migrate both localStorage and sessionStorage.
 *
 * Parameter nativeLocalStorage  → real localStorage
 * Parameter nativeSessionStorage → real sessionStorage
 */
export function migrateAllPlainBrowserStorageKeys(
  nativeLocalStorage: NativeBrowserStorage,
  nativeSessionStorage: NativeBrowserStorage
): void {
  migratePlainStorageKeys(nativeLocalStorage);
  migratePlainStorageKeys(nativeSessionStorage);
  pendingPlainKeyMigration = false;
  clearEncryptedStorageReadCache();
}

/**
 * Decryption 3.0.1 — MAIN READ. Runs when screen code calls localStorage.getItem("hawbId").
 *
 * Parameter native   → real browser storage (from patchStorage)
 * Parameter kind     → 'local' or 'session'
 * Parameter plainKey → key name from screen code, example "hawbId"
 *
 * Screen code always gets normal value like "12345".
 * Browser stores hidden name + scrambled value — screen never sees that.
 */
export function readEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string
): string | null {
  // Microsoft login keys — read directly, no decryption needed
  if (isMsalStorageKey(plainKey)) {
    return native.getItem(plainKey);
  }

  // Return from cache if we already read this key recently (faster)
  const cached = readCache.get(cacheId(kind, plainKey));
  if (cached !== undefined) {
    return cached;
  }

  // Password not ready (before login) — read plain text directly
  if (!isLocalStorageEncryptionKeyReady()) {
    return native.getItem(plainKey);
  }

  // Key 1.0.4 — get secret password from memory
  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    return native.getItem(plainKey);
  }

  try {
    // Decryption 3.0.2 — find and decrypt the value
    const value = readEncryptedValue(native, plainKey, encryptionKey);
    // Save in cache for next read
    if (value !== null) {
      readCache.set(cacheId(kind, plainKey), value);
    }
    return value;
  } catch {
    // If error, try reading plain text as backup
    return native.getItem(plainKey);
  }
}

let migrateAfterWriteScheduled = false;
let migrateAfterWriteTarget: NativeBrowserStorage | null = null;

/**
 * Migration 4.0.5 — After saving, check if any plain keys still need encrypting.
 * Parameter native → browser storage that was just written to
 */
function scheduleMigrateAfterWrite(native: NativeBrowserStorage): void {
  if (!isLocalStorageEncryptionKeyReady()) {
    return;
  }

  migrateAfterWriteTarget = native;
  if (migrateAfterWriteScheduled) {
    return;
  }

  migrateAfterWriteScheduled = true;
  // Run after current save finishes
  queueMicrotask(() => {
    migrateAfterWriteScheduled = false;
    const target = migrateAfterWriteTarget;
    migrateAfterWriteTarget = null;
    if (target) {
      migratePlainStorageKeys(target);
      clearEncryptedStorageReadCache();
    }
  });
}

/**
 * Encryption 2.0.1 — MAIN SAVE. Runs when screen code calls localStorage.setItem("hawbId", "12345").
 *
 * Parameter native   → real browser storage (from patchStorage)
 * Parameter kind     → 'local' or 'session'
 * Parameter plainKey → key name from screen code, example "hawbId"
 * Parameter value    → value from screen code, example "12345"
 *
 * What browser stores: hidden name "8f3a2b1c..." + scrambled value "U2FsdGVkX1..."
 * Screen code does not need to know about encryption — it just calls setItem normally.
 */
export function writeEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string,
  value: LocalStorageWritableValue
): void {
  // Convert value to text (localStorage only stores text)
  const normalizedValue = normalizeLocalStorageValue(value);

  // Microsoft login keys — save directly without encryption
  if (isMsalStorageKey(plainKey)) {
    native.setItem(plainKey, normalizedValue);
    return;
  }

  // Remove old cached read for this key (value is changing)
  invalidateCache(kind, plainKey);

  // Password not ready yet — save as plain text now, convert to encrypted later after login
  if (!isLocalStorageEncryptionKeyReady()) {
    native.setItem(plainKey, normalizedValue);
    schedulePlainStorageKeyMigration();
    return;
  }

  // Key 1.0.4 — get secret password from memory
  const encryptionKey = getLocalStorageEncryptionKey();
  if (!encryptionKey) {
    native.setItem(plainKey, normalizedValue);
    return;
  }

  try {
    // Step 1 — hide key name "hawbId" → "8f3a2b1c..." (Encryption 2.0.3)
    const hashedKey = hashLocalStorageKeyName(plainKey, encryptionKey);
    // Step 2 — encrypt value "12345" → "U2FsdGVkX1..." (Encryption 2.0.2 → 2.0.4)
    const encrypted = safeEncrypt(normalizedValue, encryptionKey);

    // If encryption failed, save plain text as backup
    if (encrypted === null) {
      native.setItem(plainKey, normalizedValue);
      return;
    }

    // Step 3 — save encrypted value in browser under hidden name
    native.setItem(hashedKey, encrypted);
    // Step 4 — delete old plain key name if it exists
    native.removeItem(plainKey);
    // Step 5 — check if any other plain keys need converting
    scheduleMigrateAfterWrite(native);
  } catch {
    // On error, save plain text as backup
    native.setItem(plainKey, normalizedValue);
    scheduleMigrateAfterWrite(native);
  }
}

/**
 * Encryption 2.0.6 — Remove one item. Runs when screen calls localStorage.removeItem("hawbId").
 *
 * Parameter native   → real browser storage
 * Parameter kind     → 'local' or 'session'
 * Parameter plainKey → key name from screen code
 *
 * Removes both hidden name and plain name (in case old plain data exists).
 */
export function removeEncryptedStorageItem(
  native: NativeBrowserStorage,
  kind: StorageKind,
  plainKey: string
): void {
  // Microsoft keys — remove directly
  if (isMsalStorageKey(plainKey)) {
    native.removeItem(plainKey);
    return;
  }

  invalidateCache(kind, plainKey);

  // Also remove hidden name version if password is ready
  if (isLocalStorageEncryptionKeyReady()) {
    const encryptionKey = getLocalStorageEncryptionKey();
    if (encryptionKey) {
      try {
        native.removeItem(hashLocalStorageKeyName(plainKey, encryptionKey));
      } catch {
        // Continue to remove plain name too
      }
    }
  }

  // Remove plain name if it exists
  native.removeItem(plainKey);
}

/**
 * Encryption 2.0.7 — Clear all PacTrak data. Runs when screen calls localStorage.clear().
 *
 * Parameter native → real browser storage
 * Removes all app keys. Keeps Microsoft login keys (msal.*).
 */
export function clearEncryptedStorage(native: NativeBrowserStorage): void {
  clearEncryptedStorageReadCache();

  // Collect all keys except Microsoft login keys
  const keysToRemove: string[] = [];
  for (let index = 0; index < native.length; index += 1) {
    const key = native.key(index);
    if (key && !isMsalStorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  // Remove each collected key
  keysToRemove.forEach(key => native.removeItem(key));
}
